import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Scissors, Lightbulb, Baby, User, Heart, Wind, Activity, Pill, Droplet, Brain, Eye, Bug, Dna, FileText, LogOut, LogIn, Puzzle } from 'lucide-react';
import { Patient } from '@/types/patient.types';

// Import all 14 VON form components
import { Von00PerinatalForm } from './Von00PerinatalForm';
import { Von01MaternalForm } from './Von01MaternalForm';
import { Von02ResuscitationForm } from './Von02ResuscitationForm';
import { Von03RespSupportForm } from './Von03RespSupportForm';
import { Von04RespCourseForm } from './Von04RespCourseForm';
import { Von05CardiovascularForm } from './Von05CardiovascularForm';
import { Von06GiNutritionForm } from './Von06GiNutritionForm';
import { Von07RenalForm } from './Von07RenalForm';
import { Von08NeurologyForm } from './Von08NeurologyForm';
import { Von09OphthalmologyForm } from './Von09OphthalmologyForm';
import { Von09InfectionsForm } from './Von09InfectionsForm';
import { Von11CongenitalForm } from './Von11CongenitalForm';
import { Von12GeneticForm } from './Von12GeneticForm';
import { Von13ReadmissionForm } from './Von13ReadmissionForm';
import { VonSurgeryForm } from './VonSurgeryForm';
import { Von10OutcomesForm } from './Von10OutcomesForm';
import { PrematuritateSummaryView } from './PrematuritateSummaryView';

interface PrematuritateDataTabsProps {
  patientId: number;
  patient?: Patient;
  onDataUpdated?: () => void;
  readOnly?: boolean;
}

export const PrematuritateDataTabs: React.FC<PrematuritateDataTabsProps> = ({
  patientId,
  patient,
  onDataUpdated,
  readOnly = false
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [summaryRefresh, setSummaryRefresh] = useState(0);

  const handleFormSaved = () => {
    onDataUpdated?.();
    setSummaryRefresh((prev) => prev + 1);
  };

  const handleSummaryEdit = (section: string) => {
    // Map section names to tab IDs
    const sectionMap: Record<string, string> = {
      perinatal: 'perinatal',
      maternal: 'maternal',
      resuscitation: 'resuscitation',
      resp_support: 'resp-support',
      resp_course: 'resp-course',
      cardiovascular: 'cardiovascular',
      gi_nutrition: 'gi-nutrition',
      renal: 'renal',
      neurology: 'neurology',
      ophthalmology: 'ophthalmology',
      infections: 'infections',
      congenital: 'congenital',
      genetic: 'genetic',
      readmission: 'readmission',
      outcomes: 'outcomes',
      surgery: 'surgery',
    };

    const tabId = sectionMap[section];
    if (tabId) {
      setActiveTab(tabId);
    }
  };

  // Tab configuration with icons and labels
  const tabsConfig = [
    { id: 'summary', label: 'Sumar', icon: <FileText className="h-4 w-4 mr-2" /> },
    { id: 'perinatal', label: 'Informații generale', icon: <Baby className="h-4 w-4 mr-2" /> },
    { id: 'maternal', label: 'Istoric matern', icon: <User className="h-4 w-4 mr-2" /> },
    { id: 'resuscitation', label: 'Resuscitare inițială', icon: <Heart className="h-4 w-4 mr-2" /> },
    { id: 'resp-support', label: 'Suport respirator', icon: <Wind className="h-4 w-4 mr-2" /> },
    { id: 'resp-course', label: 'Suport la 36 săptămâni', icon: <Activity className="h-4 w-4 mr-2" /> },
    { id: 'cardiovascular', label: 'Cardiovascular', icon: <Heart className="h-4 w-4 mr-2" /> },
    { id: 'gi-nutrition', label: 'Digestiv & nutriție', icon: <Pill className="h-4 w-4 mr-2" /> },
    { id: 'renal', label: 'Afectare renală', icon: <Droplet className="h-4 w-4 mr-2" /> },
    { id: 'neurology', label: 'Neurologie', icon: <Brain className="h-4 w-4 mr-2" /> },
    { id: 'ophthalmology', label: 'Oftalmologie - ROP', icon: <Eye className="h-4 w-4 mr-2" /> },
    { id: 'infections', label: 'Infecții', icon: <Bug className="h-4 w-4 mr-2" /> },
    { id: 'congenital', label: 'Malformații congenitale', icon: <Puzzle className="h-4 w-4 mr-2" /> },
    { id: 'genetic', label: 'Afecțiuni genetice', icon: <Dna className="h-4 w-4 mr-2" /> },
    { id: 'surgery', label: 'Intervenții chirurgicale', icon: <Scissors className="h-4 w-4 mr-2" /> },
    { id: 'outcomes', label: 'Rezultate externare', icon: <LogOut className="h-4 w-4 mr-2" /> },
    { id: 'readmission', label: 'Reinternare', icon: <LogIn className="h-4 w-4 mr-2" /> },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Lightbulb className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          {readOnly
            ? 'Date de prematuritate (RONN - Romanian Neonatal Network). Vizualizare doar - editarea nu este permisă pentru secțiunea MCC.'
            : 'Date de prematuritate (RONN - Romanian Neonatal Network). Selectați o secțiune din tab-uri pentru a completa sau actualiza informațiile.'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex h-auto flex-wrap w-full bg-muted p-1 rounded-md gap-1 mb-6">
          {tabsConfig.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              aria-label={tab.label}
              title={tab.label}
              className="text-xs sm:text-sm flex items-center justify-center rounded-sm px-3 py-2 bg-muted hover:bg-muted/70 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow"
            >
              {tab.icon}
              <span className="hidden sm:inline ml-1.5">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-4 mt-4">
          <PrematuritateSummaryView
            key={summaryRefresh}
            patientId={patientId}
            onEditClick={handleSummaryEdit}
            readOnly={readOnly}
          />
        </TabsContent>

        {/* Section 00: Perinatal */}
        <TabsContent value="perinatal" className="space-y-4 mt-4">
          <Von00PerinatalForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 01: Maternal */}
        <TabsContent value="maternal" className="space-y-4 mt-4">
          <Von01MaternalForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 02: Resuscitation */}
        <TabsContent value="resuscitation" className="space-y-4 mt-4">
          <Von02ResuscitationForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 03: Respiratory Support */}
        <TabsContent value="resp-support" className="space-y-4 mt-4">
          <Von03RespSupportForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 04: Respiratory Course */}
        <TabsContent value="resp-course" className="space-y-4 mt-4">
          <Von04RespCourseForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 05: Cardiovascular */}
        <TabsContent value="cardiovascular" className="space-y-4 mt-4">
          <Von05CardiovascularForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 06: GI & Nutrition */}
        <TabsContent value="gi-nutrition" className="space-y-4 mt-4">
          <Von06GiNutritionForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 07: Renal */}
        <TabsContent value="renal" className="space-y-4 mt-4">
          <Von07RenalForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 08: Neurology */}
        <TabsContent value="neurology" className="space-y-4 mt-4">
          <Von08NeurologyForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 09: Ophthalmology */}
        <TabsContent value="ophthalmology" className="space-y-4 mt-4">
          <Von09OphthalmologyForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 10: Infections */}
        <TabsContent value="infections" className="space-y-4 mt-4">
          <Von09InfectionsForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 11: Congenital */}
        <TabsContent value="congenital" className="space-y-4 mt-4">
          <Von11CongenitalForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 12: Genetic */}
        <TabsContent value="genetic" className="space-y-4 mt-4">
          <Von12GeneticForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 13: Readmission */}
        <TabsContent value="readmission" className="space-y-4 mt-4">
          <Von13ReadmissionForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        <TabsContent value="surgery" className="space-y-4 mt-4">
          <VonSurgeryForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>

        {/* Section 14: Outcomes */}
        <TabsContent value="outcomes" className="space-y-4 mt-4">
          <Von10OutcomesForm patientId={patientId} onSaved={handleFormSaved} readOnly={readOnly} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
